import { useEffect, useRef, useState } from 'react';

import { getArtifactThumbnail, putArtifactThumbnail } from '@/idb.js';

import { runQueued } from './thumbnail-queue.js';

/** Where a tile's thumbnail is: not asked for yet, being read or drawn, ready, or not to be had. */
export type ThumbnailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; blob: Blob };

/**
 * Thumbnails being read or drawn this session, by key. Two tiles of one file share one draw. A failed draw
 * stays here, so a file the renderer refuses is not downloaded again on every scroll; a reload tries again.
 */
const sessionThumbnails = new Map<string, Promise<Blob>>();

/** How many mounted tiles want each key. A queued draw that nobody wants any more is skipped. */
const tilesWanting = new Map<string, number>();

/** Thrown in place of a draw whose tiles all left before its turn came, so the queue moves on. */
class NoLongerWanted extends Error {}

/** Test-only: the session map outlives a single test. */
export function clearThumbnailSessionForTests(): void {
  sessionThumbnails.clear();
  tilesWanting.clear();
}

/** Count one more tile wanting this key; the returned function counts it out again. */
function want(key: string): () => void {
  tilesWanting.set(key, (tilesWanting.get(key) ?? 0) + 1);
  return () => {
    const remaining = (tilesWanting.get(key) ?? 1) - 1;
    if (remaining > 0) tilesWanting.set(key, remaining);
    else tilesWanting.delete(key);
  };
}

/** The draw, run only if a tile still wants it when the queue gets to it. */
function drawIfWanted(
  key: string,
  produce: () => Promise<Blob>,
): Promise<Blob> {
  return runQueued(async () => {
    if (!tilesWanting.has(key)) throw new NoLongerWanted();
    return produce();
  });
}

/**
 * The stored thumbnail, or one drawn in the render queue and then stored. A skipped draw leaves the
 * session map, so the next tile that wants the key starts it again.
 */
function loadThumbnail(
  key: string,
  produce: () => Promise<Blob>,
): Promise<Blob> {
  const pending = sessionThumbnails.get(key);
  if (pending) return pending;
  const promise = (async () => {
    const stored = await getArtifactThumbnail(key);
    if (stored) return stored;
    const drawn = await drawIfWanted(key, produce);
    await putArtifactThumbnail(key, drawn);
    return drawn;
  })();
  sessionThumbnails.set(key, promise);
  promise.then(
    () => sessionThumbnails.delete(key),
    (error: unknown) => {
      if (error instanceof NoLongerWanted) sessionThumbnails.delete(key);
    },
  );
  return promise;
}

/**
 * A tile's thumbnail, read from the browser's cache first and drawn only on a miss. `key` names the exact
 * bytes — the caller puts the file's revision in it — so a new version misses and draws again, and an old
 * one is never shown for it. `produce` downloads and draws; it runs in the shared render queue, and only
 * once `enabled` (the tile is in view).
 */
export function useCachedThumbnail(
  key: string,
  enabled: boolean,
  produce: () => Promise<Blob>,
): ThumbnailState {
  const [state, setState] = useState<ThumbnailState>({ status: 'idle' });
  /** Held in a ref so a new closure on every render does not restart the load. */
  const produceRef = useRef(produce);
  produceRef.current = produce;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const stopWanting = want(key);
    setState({ status: 'loading' });
    loadThumbnail(key, () => produceRef.current()).then(
      (blob) => {
        if (!cancelled) setState({ status: 'ready', blob });
      },
      () => {
        if (!cancelled) setState({ status: 'error' });
      },
    );
    return () => {
      cancelled = true;
      stopWanting();
    };
  }, [key, enabled]);
  return state;
}
